import { type Job } from "../../data/mock";

import { accountVerificationFrom } from "../../lib/accountVerification";

export type ApplicationStatusState = "loading" | "ready" | "error";

export type JobApplication = { id: string; status: string };

export type JobListing = Job & {
  ownerUserId?: number | null;
  status?: string | null;
  description?: string | null;
  benefits?: string | null;
  previousWork?: string | null;
  positions?: number | null;
  coverImage?: string | null;
  employerStats?: { jobsPosted: number; peopleHired: number } | null;
};

export type EmployerProfile = {
  name: string;
  initials: string;
  bio: string | null;
  city: string | null;
  profileImageUrl: string | null;
  identityVerified: boolean;
  verifiedProfessions: string[];
  memberSince: string | null;
  rating: number;
  reviewCount: number;
};

export type RelatedJob = Pick<Job, "id" | "title" | "company" | "city"> & {
  salary?: string;
  type?: string;
  tags?: string[];
  createdAt?: string;
  posted?: string;
  coverImage?: string | null;
};

export function isRelatedJob(value: unknown): value is RelatedJob {
  return typeof value === "object" && value !== null
    && "id" in value && typeof value.id === "string"
    && "title" in value && typeof value.title === "string"
    && "company" in value && typeof value.company === "string"
    && "city" in value && typeof value.city === "string"
    && (!("coverImage" in value) || value.coverImage === null || typeof value.coverImage === "string")
    && (!("tags" in value) || (Array.isArray(value.tags) && value.tags.every((tag: unknown) => typeof tag === "string")));
}

export function employerFromResponse(value: unknown): EmployerProfile | null {
  if (typeof value !== "object" || value === null || !("user" in value) || !("rating" in value)) return null;
  const user = value.user;
  const rating = value.rating;
  if (typeof user !== "object" || user === null || typeof rating !== "object" || rating === null) return null;
  if (!("name" in user) || typeof user.name !== "string") return null;
  return {
    name: user.name,
    initials: "initials" in user && typeof user.initials === "string" ? user.initials : user.name.slice(0, 1),
    bio: "bio" in user && typeof user.bio === "string" ? user.bio : null,
    city: "city" in user && typeof user.city === "string" ? user.city : null,
    profileImageUrl: "profileImageUrl" in user && typeof user.profileImageUrl === "string" ? user.profileImageUrl : null,
    ...accountVerificationFrom(user),
    memberSince: "memberSince" in user && typeof user.memberSince === "string" ? user.memberSince : null,
    rating: "average" in rating && typeof rating.average === "number" ? rating.average : 0,
    reviewCount: "count" in rating && typeof rating.count === "number" ? rating.count : 0,
  };
}

export function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
